/**
 * The period fields both views of the Promet section use (#715): a typed
 * day-first date box with its error line, the way an error is shown on a
 * field, and the hour-of-day options. The styles are the `.ngz-*` field
 * rules of ngPrometZone.css.
 *
 * An error is put on a field after the user leaves it or submits, and is
 * taken off on the next edit — never while typing; `aria-invalid` moves with
 * the visible text, and the text is tied to the control (`aria-describedby`).
 */

import { createDayFirstDateInput, type DayFirstDateInput } from '../../../../../utils/dayFirstDateInput.js';
import { hourRangeLabel } from './ngPrometZoneFormat.js';

/**
 * Put the shared typed date box and its (hidden) error line into `host`, the
 * field's wrapper that already holds the `<label for="id">`. The error line
 * is `#<id>-err`, marked `data-error="<field>"`.
 */
export function mountDayField(host: HTMLElement | null, id: string, field: string): DayFirstDateInput {
  const widget = createDayFirstDateInput({ id, className: 'ngz-date' });
  const error = document.createElement('p');
  error.className = 'ngz-field-error';
  error.id = `${id}-err`;
  error.dataset.error = field;
  error.hidden = true;
  widget.input.setAttribute('aria-describedby', error.id);
  host?.append(widget.node, error);
  return widget;
}

/** Show `message` on a field (or clear it with null): the text and `aria-invalid` together. */
export function setFieldError(control: HTMLElement | null, error: HTMLElement | null, message: string | null): void {
  if (error) {
    error.textContent = message ?? '';
    error.hidden = !message;
  }
  if (message) control?.setAttribute('aria-invalid', 'true');
  else control?.removeAttribute('aria-invalid');
}

/** The options of an hour-of-day `<select>`: »no hour«, then »00:00–01:00« … »23:00–24:00«. */
export function hourOptionsHtml(noneLabel: string): string {
  return (
    `<option value="">${noneLabel}</option>` +
    Array.from({ length: 24 }, (_, h) => `<option value="${h}">${hourRangeLabel(h)}</option>`).join('')
  );
}
